/**
 * The effect overlay.
 *
 * One element, one effect at a time, never interactive. The art itself is a
 * static printed SVG (Style Bible §2 — no gradients, no glow); the motion lives
 * here in CSS, which is why the same asset can burst, pulse or settle.
 */

import { useEffect, useState } from 'react';
import { fxUri } from './art.js';
import { FX_PLAY, type FxCue } from './fx.js';

export interface FxLayerProps {
  cue: FxCue | null;
}

export function FxLayer({ cue }: FxLayerProps): JSX.Element | null {
  const [shown, setShown] = useState<FxCue | null>(null);

  useEffect(() => {
    if (!cue) return;
    setShown(cue);
    const t = window.setTimeout(() => setShown(null), FX_PLAY[cue.kind].ms);
    return () => window.clearTimeout(t);
  }, [cue]);

  if (!shown) return null;
  const play = FX_PLAY[shown.kind];

  return (
    <div className={`fx fx--${play.anchor}`} aria-hidden="true">
      {/* `key` restarts the animation when the same effect fires twice in a row. */}
      <img
        key={shown.id}
        className={`fx__art fx__art--${shown.kind}`}
        src={fxUri(shown.kind)}
        alt=""
        style={{ animationDuration: `${play.ms}ms` }}
      />
    </div>
  );
}
